import type { IconikApiClientInterface } from '@iconik-io/iconik-api/src/client';
import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  getStorage,
  getStorageByMatchingPurpose,
} from '@iconik-io/iconik-api/src/sdk/files';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import type { StorageOptions } from './types';

/**
 * Storage settings are generated as a loose index signature, so read the flags
 * the uploader cares about by checking their runtime type.
 */
function readBooleanSetting(
  settings: { [settingKey: string]: unknown },
  key: string,
): boolean | undefined {
  const value = settings[key];
  return typeof value === 'boolean' ? value : undefined;
}

export class CachedStorageOptionsProvider {
  #storageCache = new Map<string, Promise<StorageOptions>>();

  constructor(private readonly apiClient: IconikApiClientInterface) {}

  getStorageOptions(storageId: string): Promise<StorageOptions> {
    let storageOptions = this.#storageCache.get(storageId);
    if (!storageOptions) {
      const storageRequest = unwrapResponseBody(
        getStorage({ client: this.apiClient, params: { storageId } }),
      ).catch((error) => {
        // If we get a 403 it means we do not have read access to this storage but it could be
        // that it is set as default in which case we should allow upload to it. So try to fetch
        // default storage as well and see if that matches
        if (error instanceof EndpointError && error.response.status === 403) {
          return unwrapResponseBody(
            getStorageByMatchingPurpose({
              client: this.apiClient,
              params: { purpose: 'FILES' },
            }),
          )
            .then((matchingStorage) => {
              // If the ids don't match then throw the original error
              if (matchingStorage.id !== storageId) {
                throw error;
              }
              return matchingStorage;
            })
            .catch((purposeError) => {
              this.#storageCache.delete(storageId);
              throw purposeError;
            });
        }
        this.#storageCache.delete(storageId);
        throw error;
      });

      storageOptions = storageRequest.then((storage) => {
        const { id, method, settings } = storage;
        return {
          id,
          method,
          add_uuid_to_filenames: readBooleanSetting(
            settings,
            'add_uuid_to_filenames',
          ),
          presign_md5_checksum: readBooleanSetting(
            settings,
            'presign_md5_checksum',
          ),
        };
      });

      this.#storageCache.set(storageId, storageOptions);
    }
    return storageOptions;
  }
}
